import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { RegionRestrictionService } from './region-restriction.service';

@Injectable({
  providedIn: 'root',
})
export class RegionRestrictionGuard implements CanActivate, CanActivateChild {
  constructor(
    private regionRestrictionService: RegionRestrictionService,
    private router: Router,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    const targetUrl = state.url;
    return this.checkRegionAccess(targetUrl);
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    const targetUrl = state.url;
    return this.checkRegionAccess(targetUrl);
  }

  private checkRegionAccess(targetUrl: string): Observable<boolean | UrlTree> {
    // 強制檢查限制狀態 - 如果尚未檢查或已知是限制狀態
    const isRestricted = this.regionRestrictionService.isRestricted;
    const hasChecked = this.regionRestrictionService.hasChecked;

    // 如果已經知道是受限制的，立即處理
    if (hasChecked && isRestricted) {
      return of(this.handleRestrictionResult(true, targetUrl));
    }

    // 如果還沒檢查過，執行檢查
    if (!hasChecked) {
      return this.regionRestrictionService.checkRegionRestriction().pipe(
        map((restrictedResult: boolean) => {
          return this.handleRestrictionResult(restrictedResult, targetUrl);
        }),
        catchError(() => {
          // 錯誤時為安全起見，假設受限制
          return of(this.handleRestrictionResult(true, targetUrl));
        }),
      );
    }

    // 已檢查且未受限制
    return of(true);
  }

  private handleRestrictionResult(isRestricted: boolean, targetUrl: string): boolean | UrlTree {
    if (!isRestricted) {
      return true;
    }

    // 獲取語言代碼（從目標URL或默認值）
    const urlParts = targetUrl.split('/');
    const languageCode = urlParts[1] || window.languageCode || 'en';

    // 移除查詢參數和錨點，只檢查路徑部分
    const pathOnly = targetUrl.split('?')[0].split('#')[0];

    // 嚴格檢查：只允許訪問首頁路由
    const allowedPaths = [`/${languageCode}`, `/${languageCode}/`, '/'];

    const isHomePage = allowedPaths.includes(pathOnly);

    if (isHomePage) {
      return true;
    }

    // 所有其他路由都重定向到首頁
    return this.router.createUrlTree([languageCode]);
  }
}
